/**
 * Loading the signed-in institution's data, once per screen.
 *
 * Five reads in parallel (the organization, billing, the claim campaign, the
 * latest snapshot, the team), then one of four states the screen renders:
 * loading, ready, down, or signed out, which sends the reader to /login the
 * way RequireSession does on mount. Billing is an admin route, so a viewer's
 * 403 is the designed answer and arrives as `billing: null`; a pending first
 * snapshot arrives as `metrics: null` (lib/api.ts turns the Worker's 503
 * SNAPSHOT_PENDING into that) and the dashboard renders the day-one screen.
 *
 * Each screen asks once on mount. Since Sep 11 2026 it starts from what this
 * tab already read (`lib/live-cache.ts`) and replaces it when the answer
 * lands, so moving between screens no longer flashes the splash, and a
 * session ended elsewhere, or a figure recomputed, is seen a moment later
 * rather than only on the next screen.
 */

import { useCallback, useEffect, useState } from "react";

import { LoadingSplash } from "~/app/components/LoadingSplash";
import { Button, StateCard } from "~/app/components/primitives";
import {
  getBilling,
  getCohort,
  getMembers,
  getMetrics,
  getOrganization,
  type MembersPayload,
  type SessionPayload,
} from "~/app/lib/api";
import {
  clearLiveCache,
  readTenant,
  rememberTenant,
  tenantKey,
} from "~/app/lib/live-cache";
import type { LiveInput } from "~/app/lib/live-view";
import { loginPathFor } from "~/app/lib/login";
import { navigate, useHref } from "~/app/router";

export interface LiveData extends LiveInput {
  readonly members: MembersPayload | null;
}

export type LiveState =
  | { readonly kind: "off" }
  | { readonly kind: "loading" }
  | { readonly kind: "ready"; readonly data: LiveData }
  | { readonly kind: "down"; readonly message: string };

type Loaded = LiveState | { readonly kind: "signed-out" };

async function loadLive(session: SessionPayload): Promise<Loaded> {
  const [organization, billing, cohort, metrics, members] = await Promise.all([
    getOrganization(),
    getBilling(),
    getCohort(),
    getMetrics(),
    getMembers(),
  ]);
  const all = [organization, billing, cohort, metrics, members];
  if (all.some((r) => !r.ok && r.status === 401)) return { kind: "signed-out" };
  if (!organization.ok) return { kind: "down", message: organization.error.message };
  if (!cohort.ok) return { kind: "down", message: cohort.error.message };
  if (!metrics.ok) return { kind: "down", message: metrics.error.message };
  return {
    kind: "ready",
    data: {
      session,
      organization: organization.value,
      billing: billing.ok ? billing.value : null,
      cohort: cohort.value,
      metrics: metrics.value,
      members: members.ok ? members.value : null,
    },
  };
}

/** `off` without a session: the sample host reads the fixtures and asks for nothing. */
export function useLiveTenant(session: SessionPayload | undefined): {
  state: LiveState;
  reload: () => void;
} {
  const key = session ? tenantKey(session) : null;
  const [state, setState] = useState<LiveState>(() => {
    if (!session || !key) return { kind: "off" };
    const known = readTenant(key);
    return known ? { kind: "ready", data: known } : { kind: "loading" };
  });
  const [attempt, setAttempt] = useState(0);
  const href = useHref();

  useEffect(() => {
    if (!session || !key) return;
    let cancelled = false;
    const known = readTenant(key);
    setState(known ? { kind: "ready", data: known } : { kind: "loading" });
    void loadLive(session).then((result) => {
      if (cancelled) return;
      if (result.kind === "signed-out") {
        clearLiveCache();
        navigate(loginPathFor(href), { replace: true });
        return;
      }
      if (result.kind === "ready") {
        rememberTenant(key, result.data);
        setState(result);
        return;
      }
      // Down, with figures already on screen: keep them. They are the hour's
      // reading either way, and Try again or the next screen refreshes them.
      if (!known) setState(result);
    });
    return () => {
      cancelled = true;
    };
    // The address is read at request time; a later navigation must not refetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session, key, attempt]);

  const reload = useCallback(() => setAttempt((n) => n + 1), []);
  return { state, reload };
}

/** The bare frame a screen shows before its data is here, or when it is not coming. */
export function LiveGate({ state, reload }: { state: LiveState; reload: () => void }) {
  if (state.kind === "down") {
    return (
      <div data-surface="enterprise" className="p3e-bare">
        <StateCard variant="down" title="We could not load your dashboard">
          {state.message}
        </StateCard>
        <Button variant="secondary" size="sm" onClick={reload}>
          Try again
        </Button>
      </div>
    );
  }
  return <LoadingSplash label="Loading your dashboard" />;
}
